import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  message,
  Popconfirm,
  Tag,
  Tooltip,
  Row,
  Col,
  Typography,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import { useSimpleStore } from '../stores/simpleStore';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { getApiUrl, createMediaUrlWithFallback, getThumbnailUrl, getCompanyMediaUrl, getMediaUrl } from '../config';
import unifiedApi from '../services/unifiedApi';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// 비디오 플레이어 컴포넌트
const VideoPlayer: React.FC<{
  filename: string;
  companyId: number;
  title: string;
  mimeType: string;
}> = ({ filename, companyId }) => {
  const [currentSourceIndex, setCurrentSourceIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const videoSources = createMediaUrlWithFallback(filename, companyId);

  // 디버깅: 생성된 URL 확인
  console.log('🎬 VideoPlayer URL 생성:', {
    filename,
    companyId,
    videoSources
  });

  // 컴포넌트 마운트 후 자동 재생 시도
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const playVideo = async () => {
        try {
          await video.play();
        } catch (error) {
          console.log('자동 재생 실패 (사용자 상호작용 필요):', error);
        }
      };
      
      // 비디오 로드 후 재생 시도
      const handleLoadedData = () => {
        playVideo();
      };
      
      video.addEventListener('loadeddata', handleLoadedData);
      
      return () => {
        video.removeEventListener('loadeddata', handleLoadedData);
      };
    }
  }, [currentSourceIndex]);

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const video = e.currentTarget;
    if (currentSourceIndex < videoSources.length - 1) {
      // 다음 소스 시도
      setCurrentSourceIndex(currentSourceIndex + 1);
      video.src = videoSources[currentSourceIndex + 1];
      video.load();
    }
  };

  if (currentSourceIndex >= videoSources.length) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        height: '300px',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        color: '#999'
      }}>
        <VideoCameraOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
        <Text type="secondary">비디오를 로드할 수 없습니다</Text>
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
          파일: {filename}
        </Text>
      </div>
    );
  }

  return (
    <video 
      ref={videoRef}
      src={videoSources[currentSourceIndex]}
      controls
      autoPlay
      muted
      preload="auto"
      style={{ 
        maxWidth: '100%', 
        maxHeight: '500px',
        borderRadius: '4px'
      }}
      onError={handleVideoError}
    >
      브라우저가 비디오를 지원하지 않습니다.
    </video>
  );
};

interface TargetedAd {
  id: number;
  company_id: number;
  route_id: number;
  stop_id: number;
  stop_id_external: string;
  media_id: number;
  title: string;
  start_date?: string;
  end_date?: string;
  media_title: string;
  filename: string;
  original_name: string;
  duration: number;
  mime_type: string;
  media_company_id: number;
  company_name: string;
  route_number: string;
  route_name: string;
  stop_name: string;
  stop_code: string;
  created_at: string;
}

interface CompanyRoute {
  id: number;
  route_number: string;
  route_name: string;
  company_id: number;
}

interface RouteStop {
  id: number;
  stop_name: string;
  stop_code: string;
  stop_id_external: string;
  latitude: number;
  longitude: number;
  stop_order: number;
}

interface MediaFile {
  id: number;
  title: string;
  filename: string;
  original_name: string;
  duration: number;
  mime_type: string;
}

const TargetedAdsManagement: React.FC = () => {
  const { user, selectedCompanyId, selectedCompanyName } = useSimpleStore();
  const [loading, setLoading] = useState(false);
  const [ads, setAds] = useState<TargetedAd[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [modalVisible, setModalVisible] = useState(false);
  const [editingAd, setEditingAd] = useState<TargetedAd | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<TargetedAd | null>(null);
  const [form] = Form.useForm();

  const [_companies, setCompanies] = useState<any[]>([]);
  const [routes, setRoutes] = useState<CompanyRoute[]>([]);
  const [stops, setStops] = useState<RouteStop[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<number | undefined>();
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [loadingStops, setLoadingStops] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);

  // 회사 목록 로드
  const loadCompanies = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl('/api/companies'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setCompanies(data.data);
      }
    } catch (error) {
      console.error('회사 목록 로드 에러:', error);
    }
  };

  // 타겟 광고 목록 로드
  const loadTargetedAds = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString()
      });

      if (selectedCompanyId) {
        params.append('company_id', selectedCompanyId.toString());
      }

      const response = await fetch(getApiUrl(`/api/targeted-ads?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setAds(data.data.ads);
        setPagination({
          current: data.data.pagination.page,
          pageSize: data.data.pagination.limit,
          total: data.data.pagination.total
        });
      } else {
        message.error(data.error || '타겟 광고 목록 로드 실패');
      }
    } catch (error) {
      console.error('타겟 광고 로드 에러:', error);
      message.error('타겟 광고 목록을 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // 노선 목록 로드
  const loadRoutes = async (companyId: number) => {
    try {
      setLoadingRoutes(true);
      const token = localStorage.getItem('adminToken');
      
      const params = new URLSearchParams();
      if (user?.role !== 'admin') {
        params.append('company_id', companyId.toString());
      }

      const response = await fetch(getApiUrl(`/api/company-routes?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        const filteredRoutes = user?.role === 'admin' 
          ? data.data.filter((route: CompanyRoute) => route.company_id === companyId)
          : data.data;
        setRoutes(filteredRoutes);
      } else {
        message.error('노선 목록 로드 실패');
        setRoutes([]);
      }
    } catch (error) {
      console.error('노선 로드 에러:', error);
      message.error('노선 목록을 불러오는데 실패했습니다');
      setRoutes([]);
    } finally {
      setLoadingRoutes(false);
    }
  };

  // 정류장 목록 로드
  const loadStops = async (routeId: number) => {
    try {
      setLoadingStops(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(getApiUrl(`/api/targeted-ads/routes/${routeId}/stops`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setStops(data.data.stops);
      } else {
        message.error('정류장 목록 로드 실패');
        setStops([]);
      }
    } catch (error) {
      console.error('정류장 로드 에러:', error);
      message.error('정류장 목록을 불러오는데 실패했습니다');
      setStops([]);
    } finally {
      setLoadingStops(false);
    }
  };

  // 미디어 파일 목록 로드
  const loadMediaFiles = async (companyId: number) => {
    if (!companyId) {
      setMediaFiles([]);
      return;
    }

    try {
      setLoadingMedia(true);
      const token = localStorage.getItem('adminToken');
      
      const params = new URLSearchParams();
      params.append('company_id', companyId.toString());
      params.append('include_system', 'true');
      params.append('limit', '1000');

      const response = await fetch(getApiUrl(`/api/media/list?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        // 비디오 파일만 필터링 (application/mp4도 포함)
        const videoFiles = data.data.filter((media: MediaFile) => 
          media.mime_type && (media.mime_type.startsWith('video/') || media.mime_type === 'application/mp4')
        );
        setMediaFiles(videoFiles);
      } else {
        message.error('미디어 파일 목록 로드 실패');
        setMediaFiles([]);
      }
    } catch (error) {
      console.error('미디어 파일 로드 에러:', error);
      message.error('미디어 파일 목록을 불러오는데 실패했습니다');
      setMediaFiles([]);
    } finally {
      setLoadingMedia(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      loadTargetedAds();
      loadRoutes(selectedCompanyId);
      loadMediaFiles(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadCompanies();
    }
    loadTargetedAds(pagination.current, pagination.pageSize);
  }, [user]);


  // 노선 변경 핸들러
  const handleRouteChange = (routeId: number) => {
    setSelectedRoute(routeId);
    setStops([]);
    form.setFieldsValue({ stop_id: undefined, title: '' });
    if (routeId) {
      loadStops(routeId);
      // 노선이 선택되면 해당 회사의 미디어도 다시 로드
      if (selectedCompanyId) {
        loadMediaFiles(selectedCompanyId);
      }
    }
  };

  // 정류장 변경 핸들러 (제목 자동 설정)
  const handleStopChange = (stopId: number) => {
    const selectedStop = stops.find(stop => stop.id === stopId);
    if (selectedStop) {
      form.setFieldsValue({ title: selectedStop.stop_name });
    }
  };

  // 타겟 광고 생성/수정
  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const requestData = {
        ...values,
        start_date: values.dateRange?.[0]?.format('YYYY-MM-DD') || null,
        end_date: values.dateRange?.[1]?.format('YYYY-MM-DD') || null
      };
      delete requestData.dateRange;

      let result: any;
      if (editingAd) {
        result = await unifiedApi.targetedAds.update(editingAd.id, requestData);
      } else {
        result = await unifiedApi.targetedAds.create(requestData);
      }

      if (result?.success) {
        message.success(editingAd ? '타겟 광고가 수정되었습니다' : '타겟 광고가 생성되었습니다');
        setModalVisible(false);
        setEditingAd(null);
        form.resetFields();
        loadTargetedAds();
      } else {
        message.error(result?.error || '작업 실패');
      }
    } catch (error: any) {
      console.error('타겟 광고 저장 에러:', error);

      // 에러 메시지 추출 (개선된 에러 응답 처리)
      let errorMessage = '타겟 광고 저장 실패';
      if (error?.message) {
        errorMessage = error.message;
      }
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      if (error?.response?.data?.details) {
        errorMessage += ` (${error.response.data.details})`;
      }

      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 타겟 광고 삭제
  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const response = await fetch(getApiUrl(`/api/targeted-ads/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        message.success('타겟 광고가 삭제되었습니다');
        loadTargetedAds();
      } else {
        message.error(data.error || '삭제 실패');
      }
    } catch (error) {
      console.error('타겟 광고 삭제 에러:', error);
      message.error('타겟 광고 삭제 실패');
    } finally {
      setLoading(false);
    }
  };

  // 모달 열기 (생성)
  const openCreateModal = () => {
    setEditingAd(null);
    form.resetFields();
    setModalVisible(true);
    
    if (selectedCompanyId) {
      form.setFieldsValue({ company_id: selectedCompanyId });
    }
  };

  // 모달 열기 (수정)
  const openEditModal = (ad: TargetedAd) => {
    setEditingAd(ad);
    setModalVisible(true);
    
    // 수정 모드에서는 정류장 변경만 가능
    form.setFieldsValue({
      stop_id: ad.stop_id_external, // stop_id_external 사용
      title: ad.title
    });

    // 정류장 목록 로드
    if (ad.route_id) {
      setSelectedRoute(ad.route_id);
      loadStops(ad.route_id);
    }
  };

  // 미디어 미리보기 열기
  const openMediaPreview = (ad: TargetedAd) => {
    setPreviewMedia(ad);
    setPreviewVisible(true);
  };

  const columns = [
    {
      title: '회사',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 100,
      render: (text: string) => <Tag color="blue" style={{ fontSize: '11px' }}>{text}</Tag>
    },
    {
      title: '노선',
      key: 'route',
      width: 70,
      render: (record: TargetedAd) => (
        <Tag color="green" style={{ fontSize: '11px' }}>{record.route_number}</Tag>
      )
    },
    {
      title: '정류장',
      key: 'stop',
      width: 150,
      render: (record: TargetedAd) => (
        <div>
          <Text strong style={{ fontSize: '13px' }}>{record.title}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '10px' }}>
            {record.stop_code}
          </Text>
        </div>
      )
    },
    {
      title: '미디어',
      key: 'media',
      width: 150,
      render: (record: TargetedAd) => {
        return (
          <Space size="small">
            <div 
              style={{ 
                width: '50px', 
                height: '35px', 
                borderRadius: '4px',
                overflow: 'hidden',
                border: '1px solid #d9d9d9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                cursor: 'pointer'
              }}
              onClick={() => openMediaPreview(record)}
            >
              {record.filename ? (
                record.mime_type?.startsWith('video/') || record.mime_type?.includes('mp4') ? (
                  // 비디오는 썸네일 사용 (미디어의 실제 company_id 사용)
                  <img
                    src={getThumbnailUrl(record.filename, record.media_company_id)}
                    alt={record.media_title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      // fallback으로 기본 경로 시도
                      const target = e.target as HTMLImageElement;
                      const fallbackPath = getThumbnailUrl(record.filename);
                      if (!target.src.includes('/thumbnails/thumb-')) {
                        target.src = fallbackPath;
                        return;
                      }
                      // 모두 실패하면 기본 아이콘
                      target.style.display = 'none';
                      target.parentElement!.innerHTML = '<div style="color: #999; font-size: 16px;">🎬</div>';
                    }}
                  />
                ) : (
                  // 이미지는 직접 사용 (미디어의 실제 company_id 사용)
                  <img
                    src={getCompanyMediaUrl(record.filename, record.media_company_id)}
                    alt={record.media_title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      // fallback으로 기본 경로 시도
                      const target = e.target as HTMLImageElement;
                      const fallbackPath = getMediaUrl(`media/${record.filename}`);
                      if (!target.src.includes('/media/') || target.src.includes('company_')) {
                        target.src = fallbackPath;
                        return;
                      }
                      // 모두 실패하면 기본 아이콘
                      target.style.display = 'none';
                      target.parentElement!.innerHTML = '<div style="color: #999; font-size: 16px;">📷</div>';
                    }}
                  />
                )
              ) : (
                <VideoCameraOutlined style={{ color: '#999' }} />
              )}
            </div>
            <div>
              <Text style={{ fontSize: '12px' }}>{record.media_title}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '10px' }}>
                {record.duration}초
              </Text>
            </div>
          </Space>
        );
      }
    },
    {
      title: '수정',
      key: 'edit',
      width: 60,
      render: (record: TargetedAd) => (
        <Tooltip title="정류장 변경">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            size="small"
          />
        </Tooltip>
      )
    },
    {
      title: '삭제',
      key: 'delete',
      width: 60,
      render: (record: TargetedAd) => (
        <Popconfirm
          title="이 타겟 광고를 삭제하시겠습니까?"
          onConfirm={() => handleDelete(record.id)}
          okText="삭제"
          cancelText="취소"
        >
          <Tooltip title="삭제">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Tooltip>
        </Popconfirm>
      )
    }
  ];

  return (
    <div>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    타겟 광고 관리
                  </Title>
                  <Text type="secondary">
                    특정 정류장에서만 표시되는 맞춤형 광고를 관리합니다
                  </Text>
                  <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
                    📢 선택된 운수사: <strong>{selectedCompanyName || '선택되지 않음'}</strong>
                  </div>
                </div>
                
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreateModal}
                    disabled={!selectedCompanyId}
                  >
                    타겟 광고 생성
                  </Button>
                </Space>
              </div>

              <Divider style={{ margin: '12px 0' }} />

              <Table<TargetedAd>
                columns={columns}
                dataSource={ads}
                rowKey="id"
                loading={loading}
                pagination={{
                  ...pagination,
                  showSizeChanger: true,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} / 총 ${total}개`,
                  onChange: (page, pageSize) => {
                    setPagination({ ...pagination, current: page, pageSize: pageSize || 10 });
                    loadTargetedAds(page, pageSize);
                  }
                }}
                scroll={{ x: 650 }}
                size="small"
              />
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 생성/수정 모달 */}
      <Modal
        title={editingAd ? '정류장 변경' : '타겟 광고 생성'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingAd(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          preserve={false}
        >
          {!editingAd && (
            <>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="title"
                    label="광고 제목 (정류장 선택 시 자동 입력)"
                  >
                    <Input placeholder="정류장을 선택하면 자동으로 입력됩니다" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="route_id"
                    label="노선"
                    rules={[{ required: true, message: '노선을 선택해주세요' }]}
                  >
                    <Select
                      placeholder="노선을 선택하세요"
                      loading={loadingRoutes}
                      onChange={handleRouteChange}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {routes.map(route => (
                        <Option key={route.id} value={route.id}>
                          {route.route_number} - {route.route_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                
                <Col span={12}>
                  <Form.Item
                    name="stop_id"
                    label="정류장"
                    rules={[{ required: true, message: '정류장을 선택해주세요' }]}
                  >
                    <Select
                      placeholder="정류장을 선택하세요"
                      loading={loadingStops}
                      disabled={!selectedRoute}
                      onChange={handleStopChange}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {stops.map(stop => (
                        <Option 
                          key={stop.id} 
                          value={stop.stop_id_external}
                          data-stop-name={stop.stop_name}
                        >
                          {stop.stop_name} ({stop.stop_code})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="media_id"
                    label="광고 영상"
                    rules={[{ required: true, message: '미디어 파일을 선택해주세요' }]}
                  >
                    <Select
                      placeholder="광고 영상을 선택하세요"
                      loading={loadingMedia}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {mediaFiles.map(media => (
                        <Option key={media.id} value={media.id}>
                          <Space>
                            <PlayCircleOutlined />
                            {media.title} ({media.duration}초)
                          </Space>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="dateRange"
                    label="게시 기간 (선택사항)"
                  >
                    <RangePicker
                      style={{ width: '100%' }}
                      format="YYYY-MM-DD"
                      placeholder={['시작일', '종료일']}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {editingAd && (
            <>
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                <Text type="secondary">현재 설정:</Text>
                <br />
                <Text strong>{editingAd.company_name}</Text> • <Tag color="green">{editingAd.route_number}</Tag> • <Text>{editingAd.media_title}</Text>
              </div>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="stop_id"
                    label="정류장 변경"
                    rules={[{ required: true, message: '정류장을 선택해주세요' }]}
                  >
                    <Select
                      placeholder="새로운 정류장을 선택하세요"
                      loading={loadingStops}
                      onChange={handleStopChange}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {stops.map(stop => (
                        <Option 
                          key={stop.id} 
                          value={stop.stop_id_external}
                          data-stop-name={stop.stop_name}
                        >
                          {stop.stop_name} ({stop.stop_code})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="title"
                    label="광고 제목 (정류장 선택 시 자동 입력)"
                  >
                    <Input placeholder="정류장을 선택하면 자동으로 입력됩니다" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>

      {/* 미디어 미리보기 모달 */}
      <Modal
        title="미디어 미리보기"
        open={previewVisible}
        onCancel={() => {
          setPreviewVisible(false);
          setPreviewMedia(null);
        }}
        footer={null}
        width={800}
        centered
        destroyOnClose
      >
        {previewMedia && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 16 }}>{previewMedia.media_title}</Text>
              <br />
              <Text type="secondary">
                {previewMedia.company_name} • {previewMedia.route_number}번 • {previewMedia.title}
              </Text>
              {previewMedia.mime_type?.includes('video') && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    🎬 비디오가 자동으로 재생됩니다. 볼륨을 조절하려면 음소거를 해제하세요.
                  </Text>
                </div>
              )}
            </div>
            
            {previewMedia.filename && (
              <div style={{ 
                maxWidth: '100%', 
                maxHeight: '500px', 
                overflow: 'hidden',
                borderRadius: '8px',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px'
              }}>
                {previewMedia.mime_type?.startsWith('video/') || previewMedia.mime_type?.includes('mp4') ? (
                  <VideoPlayer
                    filename={previewMedia.filename}
                    companyId={previewMedia.media_company_id}
                    title={previewMedia.media_title}
                    mimeType={previewMedia.mime_type}
                  />
                ) : (
                  <img
                    src={getCompanyMediaUrl(previewMedia.filename, previewMedia.media_company_id)}
                    alt={previewMedia.media_title}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '500px',
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = getMediaUrl(`media/${previewMedia.filename}`);
                    }}
                  />
                )}
              </div>
            )}
            
            <div style={{ marginTop: 16, textAlign: 'left' }}>
              <Space direction="vertical">
                <Text><strong>재생시간:</strong> {previewMedia.duration}초</Text>
                <Text><strong>파일형식:</strong> {previewMedia.mime_type}</Text>
                <Text><strong>원본파일명:</strong> {previewMedia.original_name}</Text>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TargetedAdsManagement;